// Content schema + defaults for the parent evrststudio.com static site.
// The admin editor writes to site_settings.web_page; a small inline script
// in /var/www/evrststudio/index.html fetches /api/site-content/web_page on
// load and overlays the saved values onto elements marked with data-cms.
//
// Defaults below mirror the current hardcoded HTML so a fresh save changes
// nothing visibly until the admin actually edits a field.

export interface WebPageStat { num: string; label: string }
export interface WebPageCard { num: string; title: string; desc: string; tags: string }
// Swappable image. `url` is what the static site uses for <img src> — defaults
// point at the shipped filenames already in /var/www/evrststudio/. Admin
// uploads land under /uploads/ on the same host so they slot straight in
// without touching CORS or path conventions.
export interface WebPageImage { url: string; alt: string }

export interface WebPageContent {
  hero: {
    counter: string
    line1: string
    line2: string
    line3: string
    desc: string
    stats: [WebPageStat, WebPageStat, WebPageStat]
    image: WebPageImage
  }
  about: {
    headlineLine1: string
    headlineLine2: string
    headlineLine3: string
    bodyP1: string
    bodyP2: string
    awardNum: string
    awardLabel: string
    image: WebPageImage
  }
  services: {
    eyebrow: string
    headlineLine1: string
    headlineLine2: string
    intro: string
  }
  prints: {
    eyebrow: string
    headlineLine1: string
    headlineLine2: string
    intro: string
    card01: WebPageCard
    card02: WebPageCard
    ctaLabel: string
  }
  // The 12 portfolio tiles in the "Work" grid, in render order. Same shape
  // per slot so the editor renders them in one list; default URLs are the
  // shipped filenames so the public site stays identical until an admin
  // swaps one.
  portfolio: {
    image01: WebPageImage; image02: WebPageImage; image03: WebPageImage; image04: WebPageImage
    image05: WebPageImage; image06: WebPageImage; image07: WebPageImage; image08: WebPageImage
    image09: WebPageImage; image10: WebPageImage; image11: WebPageImage; image12: WebPageImage
  }
  footer: {
    tagline: string
    copyright: string
  }
}

export const WEB_PAGE_DEFAULTS: WebPageContent = {
  hero: {
    counter: 'Gold Coast, Australia &nbsp;·&nbsp; Est. 2002',
    line1: 'Brands',
    line2: 'Built To',
    line3: 'Outlast.',
    desc: 'We build brands, products and digital experiences for founders who refuse to be average. Branding · eCommerce · Product Development · Creative Direction.',
    stats: [
      { num: '100M+', label: 'Revenue Generated' },
      { num: '30+',   label: 'Brands Built' },
      { num: '3×',    label: 'Award Winner' },
    ],
    image: { url: 'evrststudio.png', alt: 'EVRST Studio' },
  },
  about: {
    headlineLine1: 'Paul',
    headlineLine2: 'Everest',
    headlineLine3: 'Studios.',
    bodyP1: 'Two decades of building. Brands, products, businesses. From hand-screen-printing tees in a garage to launching a $20M apparel brand and onto building digital products with the world\'s most ambitious founders.',
    bodyP2: 'EVRST Studio is the next chapter. A place where everything I\'ve learned about brand, product and growth comes together for founders who want more than a logo.',
    awardNum: '3×',
    awardLabel: 'Industry Award<br>Winner',
    image: { url: 'Paul-Everest.png', alt: 'Paul Everest, founder and creative director of EVRST Studio' },
  },
  services: {
    eyebrow: 'What We Do',
    headlineLine1: 'Our',
    headlineLine2: 'Services.',
    intro: 'From zero to market leader. We partner with ambitious founders at every stage: concept, brand, product and growth.',
  },
  prints: {
    eyebrow: 'Custom Print Fulfilment',
    headlineLine1: 'Custom prints,',
    headlineLine2: 'Built to order.',
    intro: 'Design garments and DTF transfers in your browser at <a href="https://prints.evrststudio.com" style="color: var(--gold); text-decoration: none; border-bottom: 1px solid var(--gold);">prints.evrststudio.com</a>. Pressed in our studio, shipped fast. From a single tee to a full team kit, no minimums.',
    card01: {
      num: '01',
      title: 'Custom Garments',
      desc: 'Pick from premium tees, hoodies, polos and hats. Add artwork, pick colour, choose sizes. Live mockup before you order. We print and ship the finished piece.',
      tags: 'Apparel · Mockups · No minimums',
    },
    card02: {
      num: '02',
      title: 'DTF Transfers',
      desc: 'Press-ready DTF sheets to apply to your own garments. Multi-sheet builder with live DPI feedback. Pay by the centimetre, only what you need.',
      tags: 'DTF · Per-cm pricing · Multi-sheet',
    },
    ctaLabel: 'Open EVRST Prints →',
  },
  // Defaults match the current shipped portfolio grid (12 tiles, order
  // preserved). Each slot stores both image URL and alt text.
  portfolio: {
    image01: { url: 'EVRST-Studio-Image-11-b.png',                                  alt: 'Thrila Electric MX' },
    image02: { url: 'EVRST-Studio-Image-08-b.png',                                  alt: 'Thrila Racing Apparel' },
    image03: { url: 'be075f9b-a6dd-4de3-b6cd-c62c0b67494d_rw_1200.png',             alt: 'GW Racing Sprint Car' },
    image04: { url: 'EVRST-Studio-Image-02-b.png',                                  alt: 'Attack Life Campaign' },
    image05: { url: '68a0749c-eb51-4720-b0be-8d70281d67b1_rw_1200.png',             alt: 'Unit FMX Event' },
    image06: { url: 'dbfc84fe-7397-4565-81f3-828db4804424_rw_1200.png',             alt: 'Logo Design Portfolio' },
    image07: { url: 'd80ff372-7bdc-46c6-b4a8-57334cb98d6b_rw_1920.png',             alt: 'Warrior Spirit Custodian' },
    image08: { url: '36de526c-f0ed-4f47-b8f4-864b111cd47e_rw_1200.jpg',             alt: 'Citytrain Billboard' },
    image09: { url: '0529b0e4-f2a9-4678-a0d3-08ee2095a819_rw_1200.png',             alt: 'Thrila Racing Tee' },
    image10: { url: '2953f4aa-cbf4-4e0c-8ff7-85a3f9ae9383_rw_1200.png',             alt: 'Thrila Flat Track' },
    image11: { url: '6180d49f-45ba-4f83-964d-3ec0210f5f57.png',                     alt: 'Thrila x Kobu Swim' },
    image12: { url: '96b484ec-bf4a-455d-aa0b-0df80a22c2e0_rw_1920.png',             alt: 'Unit Matrix Experience' },
  },
  footer: {
    tagline: "We Don't Build Brands.<br>We Build Legacies.",
    copyright: '© 2026 EVRST Studio. All rights reserved. &nbsp;·&nbsp; Creative by Rex Wescombe',
  },
}

export function mergeWebContent(saved: unknown): WebPageContent {
  if (!saved || typeof saved !== 'object') return WEB_PAGE_DEFAULTS
  const out = JSON.parse(JSON.stringify(WEB_PAGE_DEFAULTS)) as WebPageContent
  function walk(target: Record<string, unknown>, src: Record<string, unknown>) {
    for (const [k, v] of Object.entries(src)) {
      if (v == null) continue
      if (Array.isArray(v)) target[k] = v
      else if (typeof v === 'object' && typeof target[k] === 'object' && !Array.isArray(target[k]))
        walk(target[k] as Record<string, unknown>, v as Record<string, unknown>)
      else target[k] = v
    }
  }
  walk(out as unknown as Record<string, unknown>, saved as Record<string, unknown>)
  return out
}
